import { useMemo } from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import ChartDateFilter from '@/components/general/ChartDateFilter';
import ExportPageButton from '@/components/general/ExportButton';
import PageTitle from '@/components/general/PageTitle';
// import SelectMembers from '@/components/general/SelectMembers';
import PulseReport from '@/components/PulseReport';
import SubmittedCheckinsReport from '@/components/SubmittedCheckinsReport';
import { PulseReport as PulseReportType, CheckinsReport, User } from '@/types/api';
import { DatePeriodFilters, checkinReportsFilters } from '@/utils/charts';
import { dateToMonthDayFormat, getArrayIntoStringByProp } from '@/utils/format';
import styles from './CheckinsReport.styles';

type CheckinsReportPageProps = {
  currentDateFilter: DatePeriodFilters;
  periodFilterInterval: {
    start: string;
    end: string;
  };
  handleChangeDateFilter: (datePeriod: DatePeriodFilters) => void;
  handleChangeCustomDateFilter: (startDate?: Date, endDate?: Date) => void;
  // handleSetUsersToFilter: (members: User[]) => void;
  checkinsReport?: CheckinsReport;
  pulseReport?: PulseReportType;
  selectedUsers?: User[];
};

const CheckinsReportPage = function ({
  pulseReport,
  checkinsReport,
  currentDateFilter,
  periodFilterInterval,
  handleChangeDateFilter,
  handleChangeCustomDateFilter,
  // handleSetUsersToFilter,
  selectedUsers,
}: CheckinsReportPageProps): JSX.Element {
  const dateIntervalFormatted = `${dateToMonthDayFormat(
    periodFilterInterval?.start
  )} - ${dateToMonthDayFormat(periodFilterInterval?.end)}`;

  const exportDocumentTitle = useMemo(() => {
    let title = 'Check-ins Report';

    if (selectedUsers) {
      const userNames = getArrayIntoStringByProp<User>({
        array: selectedUsers,
        propName: 'name',
        withSpace: true,
      });

      title += ` [${userNames}]`;
    } else {
      title += ` [All team members]`;
    }

    title += `, ${dateToMonthDayFormat(periodFilterInterval.start)} - ${dateToMonthDayFormat(
      periodFilterInterval.end
    )}`;

    return title;
  }, [periodFilterInterval.end, periodFilterInterval.start, selectedUsers]);

  return (
    <Box data-testid="checkins-report-page">
      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent={{ xs: 'flex-start', md: 'space-between' }}
        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
      >
        <PageTitle title="Check-ins Report" />

        {checkinsReport && (
          <ExportPageButton
            documentTitle={exportDocumentTitle}
            sx={{ marginTop: { xs: 2, lg: 0 } }}
          />
        )}
      </Box>

      <Grid sx={styles.chartsContainer} container spacing={2} marginTop={1}>
        <Grid item md={12} lg={12} width="100%" displayPrint="none">
          <Paper variant="outlined" sx={{ padding: 2 }}>
            <Grid container>
              <Grid item xs={12} lg={4}>
                <Typography variant="subtitle1" marginBottom={0.5}>
                  People
                </Typography>
                {/* <SelectMembers onSelectMembers={handleSetUsersToFilter} /> */}
              </Grid>

              <Grid item xs={12} lg={8} paddingLeft={{ xs: 0, lg: 4 }} marginTop={{ xs: 2, lg: 0 }}>
                <Typography variant="subtitle1" marginBottom={0.5}>
                  Time Frame{' '}
                  {currentDateFilter === DatePeriodFilters.CUSTOM_DATE && (
                    <strong data-testid="custom-date-interval">{`(${dateIntervalFormatted})`}</strong>
                  )}
                </Typography>

                <ChartDateFilter
                  hasCustomDate
                  initialCustomDates={periodFilterInterval}
                  filters={checkinReportsFilters}
                  currentFilter={currentDateFilter}
                  setNewFilter={handleChangeDateFilter}
                  setCustomDate={handleChangeCustomDateFilter}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {!checkinsReport && !selectedUsers?.length && (
          <Grid item md={12}>
            <Typography variant="h6" color="primary.light">
              No checkins data for these filtered results
            </Typography>
          </Grid>
        )}

        {checkinsReport && pulseReport && (
          <>
            <PulseReport pulseReport={pulseReport} />
            <SubmittedCheckinsReport checkinsReport={checkinsReport} />
          </>
        )}
      </Grid>
    </Box>
  );
};

export default CheckinsReportPage;
